import * as styles from "./OpportunitiesListItem.module.css";

import { IconSkeleton, RenderOnViewportEntry } from "@components/general";
import React, { lazy, useMemo } from "react";

import { ButtonLink } from "@components/ui";
import { IOpportunity } from "@models/hooks";
import { ISVGIconType } from "@models/component-props";
import { Link } from "gatsby";

/**
 * OpportunitiesListItem component displays a list of career opportunities.
 * Each opportunity is represented by a card with a title, excerpt and a
 * button linking to the opportunity.
 *
 * @param {IOpportunity} opp - The IOpportunity object for the opportunity to
 *   be displayed.
 * @param {Object[]} opportunitiesContent - An array of objects with the
 *   following structure: {type: string, excerpt: string, schemaDescription:
 *   string, content: string}. This is used to determine which icons to display
 *   in the footer of the card and which excerpt to use.
 * @returns {JSX.Element} - The rendered OpportunitiesListItem component.
 */
const OpportunitiesListItem = ({
  opp,
  opportunitiesContent,
}: {
  opp: IOpportunity;
  opportunitiesContent: {
    type: string;
    excerpt: string;
    schemaDescription: string;
    content: string;
  }[];
}) => {
  // Check if use default content is true and the post type is valid
  const useDefaultContent =
    opp.use_default_content &&
    // Partner and engineer post types can have default content as
    // these tend to be repeated quite a lot
    (opp.type === "partner" ||
      opp.type === "engineer" ||
      // Head office post types only have default content if they are
      // acquisitions or logistics roles as these can be quite common.
      // Other roles require a new description being wrote out.
      (opp.type === "head-office" &&
        (opp.head_office_sub_type === "phone-receiver" || // Acquisitions
          opp.head_office_sub_type === "spanner"))) // Logistics
      ? true
      : false;

  // Get the excerpt based on whether to use the default content or not.
  const excerpt = useDefaultContent
    ? opp.type === "head-office"
      ? opportunitiesContent.filter(
          (o) => o.type === opp.head_office_sub_type
        )[0].excerpt
      : opportunitiesContent.filter((o) => o.type === opp.type)[0].excerpt
    : opp.excerpt;

  // Set the subTypes string array
  const subTypes =
    opp.type === "head-office" // There can only be 1 sub type for head office opportunities so this is converted to a string array
      ? [opp.head_office_sub_type as string]
      : (opp.engineer_sub_type as string[]); // Partner and engineer opportunities can have multiple sub types

  // Lazy import the SVGIcon component
  const SVGIcon = lazy(() => import("../../../../../../SVGs/SVGIcon"));

  // Work out which icons to use based on the sub types
  const svgIcons = useMemo(() => {
    return subTypes.map((st, x) => {
      return (
        <SVGIcon
          key={`icon_st_id_${opp.id}_est_${x}`}
          icon={st as ISVGIconType}
          colour="green"
          width={40}
          height={40}
        />
      );
    });
  }, [subTypes]);

  return (
    <div
      key={`div_opportunity_${opp.id}`}
      className={styles.opportunitiesListItem}
    >
      {/* Title with link to opportunity */}
      <h3>
        <Link
          to={`/careers/opportunities/${opp.type}/${opp.slug}`}
          title={opp.title}
        >
          {opp.title}
        </Link>
      </h3>

      {/* Excerpt */}
      <p className={styles.opportunitiesListItemExcerpt}>{excerpt}</p>

      {/* Footer */}
      <div className={styles.opportunitiesListItemFooter}>
        <div className={styles.opportunitiesListItemFooterImages}>
          {/* Icons */}
          <RenderOnViewportEntry fallBackNode={<IconSkeleton size={40} />}>
            {svgIcons}
          </RenderOnViewportEntry>
        </div>

        {/* Button linking to opportunity */}
        <ButtonLink
          link={`/careers/opportunities/${opp.type}/${opp.slug}`}
          title="View Vacancy"
        />
      </div>
    </div>
  );
};

export default OpportunitiesListItem;
